/*global SunergoPlatform*/
import axios from 'axios';

SunergoPlatform.tableList = {};

const isRowExcluded = function(row, exclude) {
	if (!row.dataset.rowExclude) return false;
	return row.dataset.rowExclude == 'all' || row.dataset.rowExclude.includes(exclude);
};

const getArrayFromHTML = function(HTMLCollection) {
	return [].slice.call(HTMLCollection, 0);
};

const setupSearch = function (table) {
	const searchField = document.querySelector(`[data-sp-table-search="${table.name}"]`);
	if (!searchField) return console.warn('[Table] Search field not found');

	table.search = {
		value: false,
		results: [],
		column: '',
		emptyState: table.container.querySelector('[data-table-empty]'),
		bar: document.querySelector(`[data-table-search-bar="${table.name}"]`),
		buttonSearch: document.querySelector(`[data-action="sp-table-search_${table.name}"]`)
	};

	const updateFilter = function(target, initial) {
		let searchValue = target.value;
		table.search.value = searchValue;
		table.search.results = [];

		// If the search is empty, clear the filter.
		if (!searchValue || searchValue == '') {
			// Reset the search results.
			table.search.emptyState.setAttribute('hidden', true);
			table.search.value = false;
			SunergoPlatform.changeHistory.change('search', '');
			SunergoPlatform.changeHistory.change('searchCol', '');

			if (table.remote) {
				table.pagination.updateParams({ current: 1 });
				table.remote.updateParams({search: '', searchCol: '', page: 1 });

				SunergoPlatform.loading.remove('table-search');
				return;
			}

			// Reset each row.
			table.rows.forEach(row => {
				if (isRowExcluded(row, 'search')) return;
				row.classList.remove('sp-table__content-row--last');
				row.removeAttribute('data-search-hidden');
				row.removeAttribute('hidden');
			});

			if (table.pagination) table.pagination.setPage(1, true);
			SunergoPlatform.loading.remove('table-search');
			return;
		}

		// If this isn't an initial load, reset the page number and update the history.
		if (!initial) {
			SunergoPlatform.changeHistory.change('search', searchValue);
			SunergoPlatform.changeHistory.change('searchCol', table.search.column);
		}

		if (table.remote) {
			if (!initial) table.pagination.updateParams({ current: 1 });
			table.remote.updateParams({ search: searchValue, searchCol: table.search.column, page: table.pagination.current });
			SunergoPlatform.loading.remove('table-search');
			return;
		}

		// Prepare search terms.
		let regexFilter = new RegExp('\\b' + searchValue, 'ig');

		// Filter the table.
		table.rows.forEach(row => {
			if (isRowExcluded(row, 'search')) return;

			const cell = table.search.column ? row.querySelector(`[data-field="${table.search.column}"]`) : false;

			let textToSearch = '';
			if (cell) textToSearch = cell.textContent;
			else {
				for (const cell of row.cells) {
					textToSearch += ' ' + cell.textContent;
				}
			}
			row.dataset.searchHidden = !textToSearch.match(regexFilter);

			if (row.dataset.searchHidden == 'true') {
				row.setAttribute('hidden', true);
				return;
			}

			row.removeAttribute('hidden');
			table.search.results.push(row);
		});

		// Show empty state if there are no results.
		if (!table.search.results.length) table.search.emptyState.removeAttribute('hidden');
		else {
			table.search.emptyState.setAttribute('hidden', true);

			// Set the last row class.
			table.search.results[table.search.results.length - 1].classList.add('sp-table__content-row--last');
		}

		if (table.pagination) table.pagination.setPage(1, true);

		SunergoPlatform.loading.remove('table-search');
		return;
	};

	// Update the filter when the search field is changed.
	let typingTimer = false;
	searchField.addEventListener('keyup', function(event) {
		SunergoPlatform.loading.add('table-search');
		clearTimeout(typingTimer);
		typingTimer = setTimeout(updateFilter, 200, event.target);
	});

	// Setup button to open and close/clear search.
	SunergoPlatform.actionButtons.addAction(function(action, button) {
		if (action != `sp-table-search_${table.name}`) return;
		let searchBarTimer = false;

		if (button.getAttribute('aria-checked') == 'true') {
			button.setAttribute('aria-expanded', 'true');
			table.search.bar.removeAttribute('hidden');
		} else {
			const dataClosing = 'data-closing';
			
			button.setAttribute('aria-expanded', 'false');
			table.search.bar.setAttribute(dataClosing, true);

			clearTimeout(searchBarTimer);
			searchBarTimer = setTimeout(function() {
				table.search.bar.setAttribute('hidden', true);
				table.search.bar.removeAttribute(dataClosing);
			}, 200);
		} 

		if (!searchField.value) return;
		SunergoPlatform.loading.add('table-search');
		searchField.value = '';
		updateFilter(searchField, true);
	});

	// Setup search column button
	const selectColumn = document.querySelector(`[data-sp-table-search-col="${table.name}"]`);
	if (selectColumn) {
		table.search.column = selectColumn.value;
		selectColumn.addEventListener('change', function(e) {
			table.search.column = e.target.value;
			updateFilter(searchField);
		});
	}

	// Update the filter if there is a search query.
	if (searchField.value) {
		updateFilter(searchField, true);
		SunergoPlatform.changeHistory.queries.search = searchField.value;
	}
};

const setupDownload = function () {
	const downloadLinks = document.querySelectorAll('[data-download-link]');
	downloadLinks.forEach(link => {
		const ogLink = link.href;
		const tableId = link.dataset.downloadLink;
		const table = SunergoPlatform.tableList[tableId];
		
		if (!table) {
			console.warn('[Table] Download link could not find table', tableId);
			return;
		}
		link.addEventListener('click', function(event) {
			link.href = ogLink;
			let linkSuffix = '';
			if (table.search && table.search.value)
				linkSuffix += `&search=${encodeURIComponent(table.search.value)}`;
			if (table.search && table.search.column)
				linkSuffix += `&searchCol=${encodeURIComponent(table.search.column)}`;
			if (table.sort && table.sort.field && table.sort.dir)
				linkSuffix += `&sort=${encodeURIComponent(table.sort.field)}&dir=${encodeURIComponent(table.sort.dir)}`;
			link.href = `${ogLink}${linkSuffix}`;
			return true;
		});
	});
};

const dir = {
	default: 'ascending',
	reverse: 'descending'
};

const sortTable = function(table, sortIndex, sortDir, initial) {
	// Get cell value.
	const getCellValue = function(cell) {
		return cell.getAttribute('data-value') || cell.innerText;
	};

	// Get sort column and return the cell in the row.
	const getSortColumn = function(row, sortIndex) {
		if (sortIndex == -1) return row.dataset.id;
		const cell = row.cells[sortIndex];
		if (cell) return getCellValue(cell);
		return '';
	};

	try {
		const column = table.element.tHead.children[0].cells[sortIndex];

		// Update caption.
		const sortByCaption = table.element.caption.querySelector('[data-sort-by]');
		let captionText = sortIndex > -1 ? `${sortByCaption.dataset.i18nSortBy} ${column.textContent} (${sortDir}). ` : '';
	
		// Set caption
		if (sortByCaption) sortByCaption.textContent = captionText;

		// Save the sort state.
		table.sort.field = sortDir ? column.dataset.field : false;
		table.sort.dir = sortDir;

		// Update the history.
		SunergoPlatform.changeHistory.change('sort', table.sort.field);
		SunergoPlatform.changeHistory.change('dir', table.sort.dir);

		if (table.remote) {
			table.remote.updateParams({
				sort: table.sort.field || '',
				dir: table.sort.dir || ''
			});
			return;
		}

		// Get all rows to begin sorting.
		const tbody = table.element.tBodies[0];
		const reverse = sortDir == dir.reverse;

		table.rows.sort(function (a, b) {
			if (isRowExcluded(a, 'sort') || isRowExcluded(b, 'sort')) return;

			let x = getSortColumn(reverse ? b : a, sortIndex);
			let y = getSortColumn(reverse ? a : b, sortIndex);
			return isNaN(x - y) ? x.localeCompare(y) : x - y;
		});

		// Clone the table body.
		const cloneTbody = tbody.cloneNode(false);

		// Build the sorted table.
		while (table.rows.length) {
			cloneTbody.appendChild(table.rows.splice(0, 1)[0]);
		}

		// Replace with sorted table.
		table.element.replaceChild(cloneTbody, tbody);

		// Update rows in tableList.
		table.rows = getArrayFromHTML(table.element.tBodies[0].rows);

		// Update pagination
		if (table.pagination) table.pagination.setPage(initial ? table.pagination.current : false, initial);
	} catch (e) {
		console.error('[SP Table] Error sorting table.', e);
	}
};

const setupSorting = function() {
	// Setup table sorter buttons
	// https://github.com/tofsjonas/sortable/blob/main/sortable.js
	SunergoPlatform.actionButtons.addAction(function(action, button) {
		if (action != 'sp-table-sort') return false;

		// Check if column is sortable
		const column = button.parentElement;
		const parentIsColumnHeader = column.getAttribute('role') == 'columnheader';
		if (!parentIsColumnHeader) {
			console.error('[SP Table] Cannot sort as this is not a column header.');
			return true;
		}

		const tableContainer = button.closest('[data-sp-table]');
		const table = tableContainer ? SunergoPlatform.tableList[tableContainer.dataset.spTable] : false;
		if (!table){
			console.error('[SP Table] Cannot sort as table not found.');
			return true;
		}

		// Get the columns.
		const columns = column.parentElement.cells;
		let sortIndex = -1;

		// Switch the sort direction.
		let sortDir = column.getAttribute('aria-sort');
		if (sortDir == null) sortDir = dir.default;
		else if (sortDir == dir.default) sortDir = dir.reverse;
		else if (sortDir == dir.reverse) sortDir = false;

		// Set the sort attribute for each column.
		column.setAttribute('aria-sort', sortDir);
		for (let i = 0; i < columns.length; i++) {
			if (sortDir != false && columns[i] === column) sortIndex = i;
			else columns[i].removeAttribute('aria-sort');
		}

		sortTable(table, sortIndex, sortDir);

		return true;
	});
};

const initialSort = function(table) {
	const column = table.element.querySelector('[aria-sort]');
	if (!column) return;

	const sortDir = column.getAttribute('aria-sort');
	const sortIndex = column.cellIndex;

	sortTable(table, sortIndex, sortDir, true);
};

const updatePageNav = function(table) {
	// Update history.
	const historyPageNum = table.pagination.current != 1 ? table.pagination.current : '';
	SunergoPlatform.changeHistory.change('page', historyPageNum);

	// Disable pagination buttons that don't work.
	if (table.pagination.current <= 1) {
		table.pagination.actions.first.setAttribute('disabled', true);
		table.pagination.actions.prev.setAttribute('disabled', true);
	} else {
		table.pagination.actions.first.removeAttribute('disabled');
		table.pagination.actions.prev.removeAttribute('disabled');
	}
	
	if (table.pagination.current >= table.pagination.totalPages) {
		table.pagination.actions.next.setAttribute('disabled', true);
		table.pagination.actions.last.setAttribute('disabled', true);
	} else {
		table.pagination.actions.next.removeAttribute('disabled');
		table.pagination.actions.last.removeAttribute('disabled');
	}

	// Update pagination status.
	let status = '';

	if (table.pagination.totalRows > 0) {
		const statusStartRow = table.pagination.startRow + 1;
		status = statusStartRow;
		if (statusStartRow != table.pagination.endRow) status += `–${table.pagination.endRow}`;
		status += ` ${table.pagination.status.dataset.i18nItemOf} ${table.pagination.totalRows}`;
	}
	table.pagination.status.textContent = status;

	// Update pagination page numbers.
	if (table.pagination.currentPageSelect) {
		if (table.pagination.currentPageSelect.dataset.selecting != 'true')
			table.pagination.currentPageSelect.value = table.pagination.current > 0 ? table.pagination.current : 1;
	}
	if (table.pagination.totalPagesIndicator) table.pagination.totalPagesIndicator.textContent = table.pagination.totalPages > 0 ? parseInt(table.pagination.totalPages) : 1;
};

const changeToPage = function(table, page, noScroll) {
	if (!table.pagination) return console.warn('[Table] Pagination not initialized.');
	
	// Get total number of pages and rows.
	let totalPages = table.pagination.totalPages;
	let totalRows = table.pagination.totalRows;
	if (!table.remote) {
		totalRows = table.rows.filter(row => !isRowExcluded(row, 'pagination') && row.dataset.searchHidden != 'true').length;
		totalPages = Math.ceil(totalRows / table.pagination.size);
	}

	// Check if page is within range.
	if (page < 1 || totalPages == 0) page = 0;
	else if (page > totalPages) page = totalPages - 1;
	else page = page - 1;

	// Determine which rows are visible.
	let startRow = table.pagination.size * page;
	let endRow = startRow + table.pagination.size;
	if (endRow > totalRows) endRow = totalRows;

	// Check that startRow and endRow are within table bounds.
	if (startRow < 0) startRow = 0;

	// Set the current page again.
	table.pagination.current = Math.floor(startRow / table.pagination.size) + 1;

	// Scroll to top of table if enabled.
	if (!noScroll) table.element.scrollIntoView(true);

	// If remote, update the table data remotely.
	if (table.remote) {
		table.remote.updateParams({ page: table.pagination.current });
		return;
	}

	// Update the pagination parameters.
	table.pagination.updateParams({
		totalRows: totalRows,
		startRow: startRow,
		endRow: endRow
	});

	// Update the table rows.
	let currRow = -1;
	for (let i = 0; i < table.rows.length; i++) {
		const row = table.rows[i];
		if (isRowExcluded(row, 'pagination')) continue;
		if (row.dataset.searchHidden == 'true') continue;
		currRow++;

		row.dataset.paginationVisible = currRow >= startRow && currRow < endRow ? 'true' : 'false';

		if (row.dataset.paginationVisible == 'true') row.removeAttribute('hidden');
		else row.setAttribute('hidden', true);
	}
};

const setupPagination = function({ table, addedPaginationButtons, initial }) {
	let currentPage = parseInt(table.container.dataset.paginationCurrent);
	if (currentPage < 1) currentPage = 1;

	const totalRows = table.rows ? table.rows.filter(row => !isRowExcluded(row, 'pagination')).length : 0;

	table.pagination = {
		size: parseInt(table.container.dataset.paginationSize),
		totalRows: totalRows,
		startRow: 0,
		endRow: 0,
		current: currentPage,
		totalPages: 0,
		setPage: function(page, noScroll) {
			if (!page) page = this.current;
			changeToPage(table, page, noScroll);
		},
		status: table.container.querySelector('[data-pagination-status]'),
		currentPageSelect: table.container.querySelector('[data-page-select]'),
		totalPagesIndicator: table.container.querySelector('[data-total-pages]'),
		actions: {
			first: table.container.querySelector('[data-page="1"]'),
			prev: table.container.querySelector('[data-page="prev"]'),
			next: table.container.querySelector('[data-page="next"]'),
			last: table.container.querySelector('[data-page="last"]')
		},
		updateParams(newParams) {
			for (let key in newParams) {
				if (key == 'totalRows') {
					this.totalPages = Math.ceil(newParams[key] / this.size);
				}
				if (this[key] !== undefined) this[key] = newParams[key];
			}
			updatePageNav(table);
		}
	};

	if (currentPage) SunergoPlatform.changeHistory.queries.page = currentPage;

	const includedRows = table.rows.filter(row => !isRowExcluded(row, 'pagination'));
	if (table.rows) table.pagination.totalPages = Math.ceil(includedRows.length / table.pagination.size );

	if (table.pagination.currentPageSelect) {
		const inputField = table.pagination.currentPageSelect;

		// Update currentPageSelect input with current pagination to bypass autocomplete.
		inputField.value = table.pagination.current;
		
		let inputTimer = false;
		const updatePageSelect = function() {
			if (!inputField.value || inputField.value == '') return;
			if (inputField.value < 1 || inputField.value > table.pagination.totalPages) return;
			table.pagination.setPage(inputField.value);
		};

		inputField.addEventListener('focus', function() {
			inputField.dataset.selecting = 'true';
			inputField.value = '';
		});

		inputField.addEventListener('blur', function() {
			inputField.dataset.selecting = 'false';
			inputField.value = table.pagination.current;
		});

		inputField.addEventListener('input', function() {
			clearTimeout(inputTimer);
			inputTimer = setTimeout(updatePageSelect, 200);
		});
	}

	if (!addedPaginationButtons) {
		SunergoPlatform.actionButtons.addAction(function(action, button) {
			if (action != 'sp-table-page') return false;
	
			const tableContainer = button.closest('[data-pagination-size]');
			const table = tableContainer ? SunergoPlatform.tableList[tableContainer.dataset.spTable] : false;
			if (!tableContainer || !table) {
				console.warn('[SP Table] Pagination could not find table container.');
				return true;
			}
	
			const switchToPage = button.dataset.page;
			let newPageNum = 1;
	
			if (switchToPage == 'first') newPageNum = 1;
			else if (switchToPage == 'last') newPageNum = table.pagination.totalPages;
			else if (switchToPage == 'prev') newPageNum = table.pagination.current - 1;
			else if (switchToPage == 'next') newPageNum = table.pagination.current + 1;
			else if (parseInt(switchToPage)) newPageNum = parseInt(switchToPage);
	
			changeToPage(table, newPageNum);
	
			return true;
		});
	}
};

const setupSelection = function(table) {
	// Don't setup selection if already setup.
	if (table.selection) return;

	table.selection = {
		rows: {},
		isSelected: function(rowId) {
			return this.rows[rowId] ? true : false;
		},
		status: table.element.caption.querySelector('[data-rows-selected]'),
		selectRowCallback: undefined,
		callback: {
			initRow: undefined,
			selectRow: undefined
		}
	};

	
	table.selection.initRow = function(row) {
		let rowSelectType = false;
		let rowSelect = false;

		let cells = getArrayFromHTML(row.cells);
		cells.forEach(cell => {
			if (rowSelect) return;

			if (cell.dataset.rowSelect) {
				// If rowSelect is defined, setup the checkbox.
				rowSelectType = 'rowSelect';
				rowSelect = cell.querySelector('input[type="checkbox"]');

				// If row is selected, add to selected rows.
				if (row.getAttribute('aria-selected')) table.selection.selectRow(row, true);
			} else if (table.defaultUrlField && cell.dataset.field == table.defaultUrlField) {
				// If defaultUrlField is defined, setup the link.
				rowSelectType = 'link';
				rowSelect = cell.querySelector('a');
			}
		});
		if (!rowSelect) return;

		// Add the row click event.
		row.dataset.selectable = rowSelectType;
		const rowClicked = function(event) {
			// If click in the row is not the rowSelect target, click the rowSelect target.
			if (event.target != rowSelect) {
				event.preventDefault();
				rowSelect.click();
				return;
			}

			// If row is a selection, toggle the row selection.
			if (rowSelectType == 'rowSelect') table.selection.selectRow(row);
		};
		row.addEventListener('click', rowClicked);

		if (this.callback.initRow) {
			const rowId = row.dataset.id;
			this.callback.initRow({
				row, 
				rowId,
				data: table.remote.getData(rowId)
			});
		}
	};

	table.selection.init = function() {
		table.rows.forEach(row => this.initRow(row));
	};

	if (!table.selectable) return;

	table.selection.selectRow = function(row, selected) {
		// Check if row and a checkbox is present.
		if (!row) return;
		const input = row.querySelector('input[type="checkbox"]');
		if (!input) return;
		
		// If selected is not defined, set selection to toggle.
		if (selected === undefined) selected = !row.getAttribute('aria-selected');

		// Update selected status for checkbox and selected rows list.
		if (selected) {
			row.setAttribute('aria-selected', 'true');
			input.checked = true;
			this.rows[row.dataset.id] = row;
		} else {
			row.removeAttribute('aria-selected');
			input.checked = false;
			if (this.rows[row.dataset.id]) delete this.rows[row.dataset.id];
		}

		// If callback is defined, call it and don't update caption.
		if (this.callback.selectRow) {
			const rowId = row.dataset.id;
			return this.callback.selectRow({
				row,
				rowId,
				data: table.remote.getData(rowId),
				selected
			});
		}

		// Update caption if available.
		if (!this.status) return;
		const numSelected = Object.keys(this.rows).length;
		this.status.textContent = numSelected > 0 ? 
			`${numSelected} ${this.status.dataset.i18nSelected} ` : '';
	};

	table.selection.disableRow = function({row, disable, selected, label}) {
		if (!row) return;
		const input = row.querySelector('input[type="checkbox"]');
		if (!input) return;
		disable = disable == undefined ? true : disable;

		if (selected != undefined) input.checked = selected;
		input.disabled = disable;

		if (disable) row.setAttribute('data-disabled', 'true');
		else row.removeAttribute('data-disabled');

		if (label) {
			input.title = label;
			row.querySelector('label').textContent += ` (${label})`;
		}
	};
};

const initSelection = function(table) {
	table.selection.init();
};

const insertTableRow = function(table, row, index) {
	const rowClone = SunergoPlatform.templates.clone('sp-table-row');
	
	rowClone.dataset.id = row.id || index;
	for (let i = 0; i < rowClone.cells.length; i++) {
		const cell = rowClone.cells[i];

		if (cell && cell.dataset.rowSelect) {
			const input = cell.querySelector('input[type="checkbox"]');
			if (!input) continue;
			input.id = `${table.name}-row-${index}-select`;
			input.value = input.id;
			
			const label = cell.querySelector('label');
			if (!label) continue;
			label.setAttribute('for', input.id);
			label.textContent = `${label.dataset.i18nSelect}${index}`;

			if (table.selection && table.selection.isSelected(row.id)) table.selection.selectRow(rowClone, true);
		}

		if (!cell.dataset.field) continue;
		let cellData = row[cell.dataset.field];

		let cellContent = '';
		if (cell.dataset.formatter == 'date') {
			cell.dataset.value = cellData;
			cellContent = SunergoPlatform.datetimeFormatter.dateDisplayLong(cellData);
		} else if (cell.dataset.formatter == 'datetime') {
			cell.dataset.value = cellData;
			cellContent = SunergoPlatform.datetimeFormatter.dateTimeDisplay(cellData);
		} else if (cell.dataset.formatter == 'time') {
			cell.dataset.value = cellData;
			cellContent = SunergoPlatform.datetimeFormatter.timeDisplay12(cellData);
		} else if (cell.dataset.formatter == 'money' || cell.dataset.formatter == 'currency') {
			cell.dataset.value = cellData;
			cellContent = parseFloat(cellData).toFixed(2);
		} else if (cell.dataset.formatter == 'actions') {
			console.warn('[SP Table] "Actions" have not been implemented yet.');
		}

		if (!cellContent) cellContent = cellData;
		
		const link = cell.querySelector('a');
		const image = cell.querySelector('img');
		const output = cell.querySelector('[data-template-output]');
		if (link) {
			const urlField = row[link.dataset.urlField];
			const labelField = row[link.dataset.labelField] || false;
			const label = labelField ? labelField : cellContent;
			cell.dataset.value = labelField ? labelField : cellContent;

			if (urlField) {
				link.href = link.href + urlField;
				link.textContent = label;
			} else cell.textContent = label;
		} else if (image) {
			const urlField = row[image.dataset.urlField];
			image.src = urlField || cellContent;
			image.alt = row[image.dataset.altField] || '';
		} else if (output) {
			if (!cell.dataset.value) cell.dataset.value = cellContent;
			if (cellContent === undefined || cellContent === null) cell.innerHTML = '';
			else output.textContent = cellContent;
		} else cell.textContent = cellContent;
	}

	table.element.tBodies[0].appendChild(rowClone);
};

const ErrorUser = function(message) {
	this.userMsg = message;
};

let refreshCounter = 0;
const getRemoteData = function(table) {
	if (!table.remote.url) return;
	table.remote.isLoading(true);

	axios({
		method: 'get',
		url: table.remote.url,
		params: table.remote.params,
		timeout: 5000,
		responseType: 'json',
	}).then(function(response) {
		if (response.data.error) throw new ErrorUser(response.data.error);

		// Check if the page is within limits.
		const totalPages = Math.ceil(response.data.totalRows / table.pagination.size);

		// If the page is outside limits, go to the last page available.
		if (parseInt(table.pagination.current) > totalPages) {
			table.pagination.updateParams({ current: totalPages });
			if (refreshCounter < 2) table.remote.updateParams({ page: parseInt(totalPages) });
			refreshCounter ++;
			return;
		}
		refreshCounter = 0;

		table.remote.setData(response.data);
	}).catch(function(error) {
		// Insert an empty state.
		table.remote.clearTable('error');

		let msg = error.userMsg ? error.userMsg : false;
		const tableErrorMsg = table.element.querySelector('[data-table-error-msg]');
		if (tableErrorMsg) {
			if (msg) tableErrorMsg.textContent = msg;
			else tableErrorMsg.textContent = tableErrorMsg.dataset.tableErrorMsg;
		}

		console.error('[SP Table] Error getting remote data\n', {error});
		table.remote.isLoading(false);
	});
};

const setupRemoteTable = function(table) {
	table.remote = {
		data: {},
		loadingId: `table-remote ${table.name}`,
		url: table.container.dataset.remote ? encodeURI(table.container.dataset.remote) : false,
		emptyState: {
			default: SunergoPlatform.templates.clone('sp-table-row__empty'),
			error: SunergoPlatform.templates.clone('sp-table-row__error'),
			search: table.searchable ? SunergoPlatform.templates.clone('sp-table-row__search-empty') : false,
		},
		params: {
			page: table.pagination.current,
			size: table.pagination.size,
			search: table.search ? table.search.value || '' : '',
			searchCol: table.search ? table.search.column || '' : '',
			sort: false,
			dir: ''
		},
		updateParams: function(newParams, getData = true) {
			for (let key in newParams) {
				let newParam = newParams[key];
				if (key === 'page' && newParam < 1) newParam = 1;
				if (this.params[key] !== undefined) this.params[key] = encodeURI(newParam);
			}
			if (getData) getRemoteData(table);
		},
		setData: function({ rows = [], totalRows = 0, searchResults = false }) {
			const data = this.data;
			this.isLoading(true);

			// Get the start row based on pagination size.
			const startRow = (table.pagination.current - 1) * table.pagination.size;
			let index = startRow || 0;

			if (!rows || rows.length < 1) {
				// Show any empty state if there are no rows.
				if (searchResults) this.clearTable('search');
				else this.clearTable();
			} else {
				this.clearTable(false);
				rows.forEach(row => {
					index ++;

					// Save the raw data.
					const rowId = row.id || index;
					data[rowId] = row;

					// Insert the row.
					insertTableRow(table, row, index);
				});
			}

			// Save the rows.
			table.rows = getArrayFromHTML(table.element.tBodies[0].rows);
			initSelection(table);

			// Update the pagination.
			if (totalRows !== undefined && table.pagination) {
				table.pagination.updateParams({
					totalRows: totalRows,
					startRow: startRow,
					endRow: index,
				});
			}

			this.isLoading(false);
		},
		getData: function(id) {
			const data = this.data[id];
			if (!data) {
				console.warn('[SP Table] No data for row', id);
				return {};
			} else return data;
		},
		clearTable: function(state) {
			state = state != false ? state || 'default' : false;
			table.element.tBodies[0].innerHTML = '';
			table.rows = [];
			if (state) table.element.tBodies[0].appendChild(table.remote.emptyState[state]);
		},
		isLoading: function(state) {
			table.container.setAttribute('aria-busy', state);
			if (state) SunergoPlatform.loading.add(this.loadingId);
			else SunergoPlatform.loading.remove(this.loadingId);
		}
	};
	if (table.remote.url == 'undefined') table.remote.url = false;
};

const setupSPTables = function(tables) {
	let hasSorting = false;

	tables.forEach(container => {
		const tableName = container.dataset.spTable;
		const tableElement = container.querySelector('table');

		SunergoPlatform.tableList[tableName] = {
			name: tableName,
			container: container,
			element: tableElement,
			rows: tableElement ? getArrayFromHTML(tableElement.tBodies[0].rows) : [],
			searchable: container.dataset.searchable,
			downloadable: container.dataset.downloadable,
			sortable: container.dataset.sortable,
			pagination: container.dataset.paginationSize > 0,
			defaultUrlField: container.dataset.defaultUrlField,
			selectable: container.dataset.selectable,
			remote: container.dataset.remote != undefined
		};
		const table = SunergoPlatform.tableList[tableName];
		setupSelection(table);
		
		if (table.sortable) {
			hasSorting = true;
			table.sort = {
				field: container.dataset.sortField,
				dir: container.dataset.sortDir,
			};
		}
	});

	if (hasSorting) setupSorting();
};

function setupTables() {
	const tabulators = document.querySelectorAll('[data-tabulator]');
	if (tabulators.length > 0) console.error('[SP Table] Tabulator is not supported. Please use SP Table instead.');

	const spTable = document.querySelectorAll('[data-sp-table]');
	if (spTable.length > 0) setupSPTables(spTable);
}

const initTables = function() {
	let downloadable = false;
	let addedPaginationButtons = false;

	for (const [key, table] of Object.entries(SunergoPlatform.tableList)) {
		if (table.downloadable) downloadable = true;
		if (table.pagination) {
			setupPagination({ table, addedPaginationButtons, initial: true });
			addedPaginationButtons = true;
		}
		if (table.remote) setupRemoteTable(table);
		if (table.selectable || table.defaultUrlField) initSelection(table);
		if (table.searchable) setupSearch(table);
		if (table.sortable) initialSort(table);
		if (table.remote) getRemoteData(table);
	}

	if (downloadable) setupDownload();
};

// Run scripts on document loaded
SunergoPlatform.onLoad.push(setupTables, initTables);
